import React, { Fragment, useEffect, useMemo, useState } from "react";
import { IRoutePricePreview } from "./RoutePriceComponent";
import RoutePricePreviewCard from "./RoutePricePreviewCard";
import RoutePricePreviewTable from "./RoutePricePreviewTable";

interface IPricePreview {
  routePrices: IRoutePricePreview[];
  viewType?: "card" | "table";
}

const RoutePricePreview = ({
  routePrices,
  viewType = "card",
}: IPricePreview) => {
  const [expandedMapper, setExpandedMapper] = useState<Record<string, boolean>>(
    {},
  );

  // grouping of routePrices by vehicleSize
  const grouped = useMemo(() => {
    const result: Record<string, IRoutePricePreview[]> = {};

    for (const entry of routePrices) {
      if (!result[entry.vehicleSize]) {
        result[entry.vehicleSize] = [];
      }

      result[entry.vehicleSize].push(entry);
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePrices]);

  // Expand all cards by default
  useEffect(() => {
    if (Object.keys(expandedMapper).length > 0) return;

    // Pre-expand all cards based on item count for each group
    const newExpanded: Record<string, boolean> = {};
    Object.entries(grouped).forEach(([vehicleSize, entries]) => {
      const lastCount = entries.filter((e) => e.type === "last").length;
      const newCount = entries.filter((e) => e.type === "new").length;
      const count = Math.max(lastCount, newCount);

      for (let i = 0; i < count; i++) {
        const key = `${vehicleSize}-${i}`;
        newExpanded[key] = true;
      }
    });

    setExpandedMapper(newExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped]);

  // Toggle expand/collapse for card
  const toggleExpanded = (key: string) => {
    setExpandedMapper((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <Fragment>
      {Object.entries(grouped).map(([vehicleSize, entries]) => {
        // Split data into last month preview and new preview entries and
        // Render rows based on the max length between both
        const lastDatas = entries.filter((e) => e.type === "last");
        const currentDatas = entries.filter((e) => e.type === "new");
        const itemCount = Math.max(lastDatas.length, currentDatas.length);

        switch (viewType) {
          case "card":
            return Array.from({ length: itemCount }).map((_, index) => {
              const key = `${vehicleSize}-${index}`;
              return (
                <RoutePricePreviewCard
                  key={key}
                  vehicleSize={vehicleSize}
                  index={index}
                  lastData={lastDatas[index]}
                  currentData={currentDatas[index]}
                  expanded={expandedMapper[key] ?? false}
                  onToggle={() => toggleExpanded(key)}
                />
              );
            });

          case "table":
            return Array.from({ length: itemCount }).map((_, index) => {
              const key = `${vehicleSize}-${index}`;
              return (
                <RoutePricePreviewTable
                  key={key}
                  vehicleSize={vehicleSize}
                  index={index}
                  lastData={lastDatas[index]}
                  currentData={currentDatas[index]}
                  expanded={expandedMapper[key] ?? false}
                  onToggle={() => toggleExpanded(key)}
                />
              );
            });

          default:
            return (
              <Fragment key={vehicleSize}>
                Invalid viewType: {viewType}
              </Fragment>
            );
        }
      })}
    </Fragment>
  );
};

export default RoutePricePreview;
